import { useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { DataFieldFormValue } from '@toolpad/core';
import RemoveIcon from '@mui/icons-material/Remove';
import { Card, MenuItem, TextField, IconButton } from '@mui/material';

import { Role } from 'types';
import { mapQueries } from 'utils/query';
import { Details } from 'components/details';
import { createFieldArray } from 'utils/form';
import { useAdminTerminology } from 'hooks/use-admin-terminology';
import {
  Nullable,
  ReportsData,
  ReportConfig,
  ReportConfigWithRoles,
  DataFieldRenderFormFieldWithLabel,
} from 'types';

type ReportItem<TType extends string, TRole extends string> =
  ReportConfig<TType> | ReportConfigWithRoles<TType, TRole>;

type Props<TType extends string, TRole extends string> = {
  title?: string;
  defaultType: TType;
  typeOptions: TType[];
  typesWithRoles: TType[];
  availableRoles: TRole[];
  error?: Nullable<string>;
  typeTitles: Record<TType, string>;
  roleTitles: Record<TRole, string>;
  value: ReportItem<TType, TRole>[];
  onChange: (value: ReportItem<TType, TRole>[]) => void;
};

const hasRoles = <TType extends string>(type: TType, typesWithRoles: TType[]) =>
  typesWithRoles.includes(type);

const isReportsDataValue = <TType extends string, TRole extends string>(
  value: unknown
): value is ReportsData<ReportItem<TType, TRole>> => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return false;
  }

  return Array.isArray((value as { configs?: unknown }).configs);
};

export const ReportConfigsFieldArray = <
  TType extends string,
  TRole extends string,
>({
  value,
  error,
  onChange,
  typeTitles,
  roleTitles,
  typeOptions,
  defaultType,
  typesWithRoles,
  availableRoles,
  title = 'Config',
}: Props<TType, TRole>) => {
  const { queries, terminology } = useAdminTerminology();
  const resolvedRoleTitles = useMemo(
    () => ({
      ...roleTitles,
      [Role.TENANT_GUEST]: terminology.roleTitles[Role.TENANT_GUEST],
    }),
    [roleTitles, terminology]
  );
  const FieldArray = useMemo(
    () =>
      createFieldArray<ReportItem<TType, TRole>>(
        () =>
          ({
            name: '',
            id: uuidv4(),
            type: defaultType,
            ...(hasRoles(defaultType, typesWithRoles) ? { roles: [] } : {}),
          }) as ReportItem<TType, TRole>,
        (item, _index, onItemChange, onRemove) => (
          <Card variant="outlined" className="flex w-full gap-4 p-4">
            <div className="flex flex-1 flex-col gap-4">
              <TextField
                fullWidth
                label="Name"
                value={item.name}
                onChange={(e) =>
                  onItemChange({ ...item, name: e.target.value })
                }
              />
              <TextField
                select
                fullWidth
                label="Type"
                value={item.type}
                onChange={(e) => {
                  const type = e.target.value as TType;
                  const next: Record<string, unknown> = {
                    ...item,
                    type,
                  };

                  if (hasRoles(type, typesWithRoles)) {
                    next.roles = Array.isArray(
                      (item as { roles?: TRole[] }).roles
                    )
                      ? (item as { roles?: TRole[] }).roles
                      : [];
                  } else {
                    delete next.roles;
                  }

                  onItemChange(next as unknown as ReportItem<TType, TRole>);
                }}
              >
                {typeOptions.map((type) => (
                  <MenuItem key={type} value={type}>
                    {typeTitles[type]}
                  </MenuItem>
                ))}
              </TextField>

              {hasRoles(item.type, typesWithRoles) && (
                <TextField
                  select
                  fullWidth
                  label="Roles"
                  slotProps={{ select: { multiple: true } }}
                  value={
                    (item as ReportConfigWithRoles<TType, TRole>).roles || []
                  }
                  onChange={(e) =>
                    onItemChange({
                      ...item,
                      roles: e.target.value as unknown as TRole[],
                    } as ReportItem<TType, TRole>)
                  }
                >
                  {availableRoles.map((role) => (
                    <MenuItem key={role} value={role}>
                      {resolvedRoleTitles[role]}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            </div>

            <div className="flex items-center justify-center">
              <IconButton size="small" onClick={onRemove}>
                <RemoveIcon fontSize="small" />
              </IconButton>
            </div>
          </Card>
        )
      ),
    [
      defaultType,
      typeOptions,
      typeTitles,
      resolvedRoleTitles,
      typesWithRoles,
      availableRoles,
    ]
  );

  return (
    <Details {...mapQueries(queries)}>
      <FieldArray
        title={title}
        value={value}
        error={error}
        onChange={(next) =>
          onChange(next as unknown as ReportItem<TType, TRole>[])
        }
      />
    </Details>
  );
};

export const renderReportConfigsFormField = <
  TType extends string,
  TRole extends string,
>({
  typeTitles,
  roleTitles,
  defaultType,
  typeOptions,
  typesWithRoles,
  availableRoles,
  title = 'Config',
}: {
  title?: string;
  defaultType: TType;
  typeOptions: TType[];
  typesWithRoles: TType[];
  availableRoles: TRole[];
  typeTitles: Record<TType, string>;
  roleTitles: Record<TRole, string>;
}): DataFieldRenderFormFieldWithLabel => {
  return ({ value, error, onChange }) => {
    const data = isReportsDataValue<TType, TRole>(value)
      ? value
      : ({ configs: [] } as ReportsData<ReportItem<TType, TRole>>);
    const values = data.configs || [];

    return (
      <div className="w-full">
        <p className="mb-4">Reports configuration</p>
        <ReportConfigsFieldArray
          title={title}
          error={error}
          value={values}
          roleTitles={roleTitles}
          typeTitles={typeTitles}
          defaultType={defaultType}
          typeOptions={typeOptions}
          availableRoles={availableRoles}
          typesWithRoles={typesWithRoles}
          onChange={(configs) =>
            onChange({ ...data, configs } as unknown as DataFieldFormValue)
          }
        />
      </div>
    );
  };
};
